/**
 * Notes store - manages note state with optimistic updates, IndexedDB cache,
 * offline sync queue, and conflict-aware remote change handling.
 */

import { create } from 'zustand';
import type { Note, CreateNoteInput, UpdateNoteInput } from '@anna-calendar/shared';
import * as api from '../api/client';
import { cacheNotes, getCachedNotes, cacheNote, removeCachedNote, enqueueMutation } from '../db/indexeddb';
import { useAuthStore } from './auth';
import { useAppStore } from './app';
import { useSyncStore } from './sync';
import { useConflictsStore } from './conflicts';
import { getQueuedMutations } from '../db/indexeddb';

interface NotesState {
  notes: Note[];
  isLoading: boolean;
  error: string | null;

  fetchNotes: (params?: { date?: string }) => Promise<void>;
  createNote: (data: CreateNoteInput) => Promise<Note | null>;
  updateNote: (id: string, data: UpdateNoteInput) => Promise<Note | null>;
  deleteNote: (id: string) => Promise<boolean>;
  applyRemoteChange: (action: string, note: Note | { id: string }) => void;
  loadFromCache: (params?: { date?: string }) => Promise<void>;
}

export const useNotesStore = create<NotesState>((set, get) => ({
  notes: [],
  isLoading: false,
  error: null,

  fetchNotes: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const notes = await api.fetchWithAuthRetry(
        () => useAuthStore.getState().token,
        (token) => api.fetchNotes(token, params),
      );
      set({ notes, isLoading: false });
      await cacheNotes(notes);
    } catch (err: unknown) {
      if (err instanceof api.RefreshFailedError) {
        useAuthStore.getState().logout();
        set({ isLoading: false, error: 'Session expired' });
        return;
      }
      if (err instanceof api.ApiError && err.status === 401) {
        set({ isLoading: false, error: 'Session expired' });
        return;
      }
      const cached = await getCachedNotes(params);
      set({ notes: cached, isLoading: false, error: null });
    }
  },

  createNote: async (data) => {
    const tempId = crypto.randomUUID();
    const optimistic: Note = {
      ...data,
      id: tempId,
      user_id: '',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    set((s) => ({ notes: [...s.notes, optimistic] }));
    await cacheNote(optimistic);

    try {
      const created = await api.fetchWithAuthRetry(
        () => useAuthStore.getState().token,
        (token) => api.createNote(token, data),
      );
      set((s) => ({
        notes: s.notes.map((n) => (n.id === tempId ? created : n)),
      }));
      await removeCachedNote(tempId);
      await cacheNote(created);
      return created;
    } catch (err) {
      if (err instanceof api.RefreshFailedError) {
        useAuthStore.getState().logout();
        set((s) => ({ notes: s.notes.filter((n) => n.id !== tempId) }));
        await removeCachedNote(tempId);
        return null;
      }
      // Always queue failed mutations for retry
      await enqueueMutation('POST', '/api/notes', { ...data, id: tempId });
      const q = await getQueuedMutations();
      useSyncStore.getState().setPendingCount(q.length);
      set({ error: 'Note saved locally. Will sync when connected.' });
      return optimistic;
    }
  },

  updateNote: async (id, data) => {
    const prev = get().notes.find((n) => n.id === id);
    set((s) => ({
      notes: s.notes.map((n) =>
        n.id === id ? { ...n, ...data, updated_at: new Date().toISOString() } : n
      ),
    }));

    const conflicts = useConflictsStore.getState();
    conflicts.addPending(id);

    try {
      const updated = await api.fetchWithAuthRetry(
        () => useAuthStore.getState().token,
        (token) => api.updateNote(token, id, data),
      );

      const current = get().notes.find((n) => n.id === id);
      if (!current || updated.updated_at >= current.updated_at) {
        set((s) => ({
          notes: s.notes.map((n) => (n.id === id ? updated : n)),
        }));
        await cacheNote(updated);
      }

      processDeferredChange(id);
      conflicts.removePending(id);
      return updated;
    } catch (err) {
      conflicts.removePending(id);
      if (err instanceof api.RefreshFailedError) {
        useAuthStore.getState().logout();
        if (prev) {
          set((s) => ({ notes: s.notes.map((n) => (n.id === id ? prev : n)) }));
        }
        processDeferredChange(id);
        return null;
      }
      // Always queue failed mutations for retry
      await enqueueMutation('PUT', `/api/notes/${id}`, data);
      const q = await getQueuedMutations();
      useSyncStore.getState().setPendingCount(q.length);
      processDeferredChange(id);
      return prev ? { ...prev, ...data } : null;
    }
  },

  deleteNote: async (id) => {
    const prev = get().notes.find((n) => n.id === id);
    set((s) => ({ notes: s.notes.filter((n) => n.id !== id) }));

    const conflicts = useConflictsStore.getState();
    conflicts.addPending(id);

    try {
      await api.fetchWithAuthRetry(
        () => useAuthStore.getState().token,
        (token) => api.deleteNote(token, id),
      );
      await removeCachedNote(id);
      conflicts.removePending(id);
      return true;
    } catch (err) {
      conflicts.removePending(id);
      if (err instanceof api.RefreshFailedError) {
        useAuthStore.getState().logout();
        if (prev) set((s) => ({ notes: [...s.notes, prev] }));
        return false;
      }
      // Always queue failed mutations for retry
      await enqueueMutation('DELETE', `/api/notes/${id}`, null);
      const q = await getQueuedMutations();
      useSyncStore.getState().setPendingCount(q.length);
      await removeCachedNote(id);
      return true;
    }
  },

  applyRemoteChange: (action, noteOrId) => {
    const conflicts = useConflictsStore.getState();

    if (action === 'create' || action === 'update') {
      const note = noteOrId as Note;

      if (conflicts.isPending(note.id)) {
        conflicts.deferChange(note.id, {
          entity: 'note',
          action,
          data: note as unknown as Record<string, unknown>,
          timestamp: note.updated_at,
        });
        return;
      }

      set((s) => {
        const idx = s.notes.findIndex((n) => n.id === note.id);
        if (idx >= 0) {
          const local = s.notes[idx];
          if (local.updated_at > note.updated_at) return s;
          if (local.content !== note.content || local.color !== note.color || local.date !== note.date) {
            conflicts.addConflict({
              entityType: 'note',
              entityTitle: note.content.slice(0, 40) + (note.content.length > 40 ? '...' : ''),
            });
          }
          const next = [...s.notes];
          next[idx] = note;
          return { notes: next };
        }
        return { notes: [...s.notes, note] };
      });
      cacheNote(note);
    } else if (action === 'delete') {
      const { id } = noteOrId as { id: string };

      if (conflicts.isPending(id)) {
        conflicts.deferChange(id, {
          entity: 'note',
          action: 'delete',
          data: { id } as Record<string, unknown>,
          timestamp: new Date().toISOString(),
        });
        return;
      }

      set((s) => ({ notes: s.notes.filter((n) => n.id !== id) }));
      removeCachedNote(id);
    }
  },

  loadFromCache: async (params) => {
    const cached = await getCachedNotes(params);
    set({ notes: cached });
  },
}));

function processDeferredChange(entityId: string): void {
  const conflicts = useConflictsStore.getState();
  const deferred = conflicts.popDeferred(entityId);
  if (!deferred) return;

  if (deferred.action === 'delete') {
    useNotesStore.getState().applyRemoteChange('delete', { id: entityId });
  } else {
    const remoteData = deferred.data as unknown as Note;
    const local = useNotesStore.getState().notes.find((n) => n.id === entityId);
    if (!local || remoteData.updated_at >= local.updated_at) {
      if (local && (local.content !== remoteData.content || local.color !== remoteData.color)) {
        conflicts.addConflict({
          entityType: 'note',
          entityTitle: remoteData.content.slice(0, 40) + (remoteData.content.length > 40 ? '...' : ''),
        });
      }
      useNotesStore.getState().applyRemoteChange(deferred.action, remoteData);
    }
  }
}
